import { useMutation, useQueryClient } from '@tanstack/react-query'
import api, { IOrdinal } from 'api/api'
import cn from 'classnames'
import React, { useEffect, useMemo, useState } from 'react'
import { Eye, EyeOff } from 'lucide-react'

type Props = {
    ordinalId?: string | number | null
    initialValue?: boolean
    className?: string
    activeClassName?: string
    animateClassName?: string
    showLabel?: boolean
    size?: number
}

export default function HiddenButton({
    ordinalId,
    initialValue,
    className,
    activeClassName,
    animateClassName,
    showLabel = true,
    size = 16,
}: Props) {
    const [active, setActive] = useState(!!initialValue)
    const [animate, setAnimate] = useState(false)
    const queryClient = useQueryClient()

    useEffect(() => {
        setActive(!!initialValue)
    }, [initialValue])

    const id = useMemo(() => {
        const raw = ordinalId === null || ordinalId === undefined ? '' : String(ordinalId).trim()
        return raw || null
    }, [ordinalId])

    const mutation = useMutation({
        mutationFn: async () => {
            if (!id) throw new Error('Missing ordinal id')
            return await api.toggleOrdinalHidden(id)
        },
        onMutate: async () => {
            const prev = active
            setActive(!prev)
            return { prev }
        },
        onError: (_err, _vars, ctx) => {
            setActive(!!ctx?.prev)
        },
        onSuccess: res => {
            const next = !!res?.users_ordinal?.hidden
            setActive(next)

            const userId = gon.currentUser?.id
            if (!userId) return

            queryClient.setQueryData(
                ['treechatOrdinals', userId],
                (old: IOrdinal[] | undefined) => {
                    if (!Array.isArray(old)) return old
                    return old.map((o: IOrdinal) => {
                        if (String(o?.id || '') !== String(res?.id || '')) return o
                        return { ...o, users_ordinal: res.users_ordinal }
                    })
                },
            )
        },
    })

    const disabled = !id || mutation.isPending

    return (
        <button
            type="button"
            className={cn(className, active && activeClassName, animate && animateClassName)}
            onClick={e => {
                e.stopPropagation()
                e.preventDefault()
                if (disabled) return
                setAnimate(true)
                window.setTimeout(() => setAnimate(false), 350)
                mutation.mutate()
            }}
            disabled={disabled}
            aria-pressed={active}
            title={active ? 'Hidden' : 'Hide'}
        >
            {active ? <Eye size={size} /> : <EyeOff size={size} />}
            {showLabel && <span>{active ? 'Hidden' : 'Hide'}</span>}
        </button>
    )
}
